import { isLevelId } from '../data/ids';
import {
  JUMP_GRADES,
  SAVE_LIMITS,
  SCHEMA_VERSION,
  SLOT_IDS,
  type JumpGrade,
  type LevelId,
  type LevelRecord,
  type SaveFile,
  type SaveSlot,
  type Settings,
  type SlotId,
} from './schema';

export interface SaveError {
  /** Dot-separated path of the offending field; "" is the document itself. */
  path: string;
  message: string;
}

export type SaveValidation = { ok: true; value: SaveFile } | { ok: false; errors: SaveError[] };

const ISO_8601 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

type Obj = Record<string, unknown>;

const isObject = (value: unknown): value is Obj =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Collects errors while building a copy that holds only the fields the schema knows (forward tolerance). */
class Checker {
  readonly errors: SaveError[] = [];

  fail(path: string, message: string): undefined {
    this.errors.push({ path, message });
    return undefined;
  }

  object(value: unknown, path: string): Obj | undefined {
    return isObject(value) ? value : this.fail(path, 'debe ser un objeto');
  }

  int(value: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number | undefined {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
      return this.fail(path, `debe ser un entero entre ${min} y ${max}`);
    }
    return value;
  }

  unit(value: unknown, path: string): number | undefined {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) {
      return this.fail(path, 'debe ser un número entre 0 y 1');
    }
    return value;
  }

  bool(value: unknown, path: string): boolean | undefined {
    return typeof value === 'boolean' ? value : this.fail(path, 'debe ser booleano');
  }

  date(value: unknown, path: string): string | undefined {
    if (typeof value === 'string' && ISO_8601.test(value) && !Number.isNaN(Date.parse(value))) return value;
    return this.fail(path, 'debe ser una fecha ISO 8601');
  }

  levelId(value: unknown, path: string): LevelId | undefined {
    return typeof value === 'string' && isLevelId(value) ? value : this.fail(path, 'debe ser un nivel de 1-1 a 4-3');
  }
}

function checkSettings(c: Checker, input: unknown, path: string): Settings | undefined {
  const o = c.object(input, path);
  if (!o) return undefined;
  const musicVolume = c.unit(o.musicVolume, `${path}.musicVolume`);
  const sfxVolume = c.unit(o.sfxVolume, `${path}.sfxVolume`);
  const reducedMotion = c.bool(o.reducedMotion, `${path}.reducedMotion`);
  const assistMode = c.bool(o.assistMode, `${path}.assistMode`);
  const showFps = c.bool(o.showFps, `${path}.showFps`);
  if (
    musicVolume === undefined ||
    sfxVolume === undefined ||
    reducedMotion === undefined ||
    assistMode === undefined ||
    showFps === undefined
  ) {
    return undefined;
  }
  return { musicVolume, sfxVolume, reducedMotion, assistMode, showFps };
}

function checkRecord(c: Checker, input: unknown, path: string): LevelRecord | undefined {
  const o = c.object(input, path);
  if (!o) return undefined;
  const bestScore = c.int(o.bestScore, `${path}.bestScore`, 0);
  const cleanRun = c.bool(o.cleanRun, `${path}.cleanRun`);
  const assisted = c.bool(o.assisted, `${path}.assisted`);
  let bestJump: JumpGrade | null | undefined;
  if (o.bestJump === null || (JUMP_GRADES as readonly unknown[]).includes(o.bestJump)) {
    bestJump = o.bestJump as JumpGrade | null;
  } else {
    c.fail(`${path}.bestJump`, `debe ser null o uno de ${JUMP_GRADES.join(', ')}`);
  }
  if (bestScore === undefined || cleanRun === undefined || assisted === undefined || bestJump === undefined) return undefined;
  return { bestScore, bestJump, cleanRun, assisted };
}

function checkUnlocked(c: Checker, input: unknown, path: string): LevelId[] | undefined {
  if (!Array.isArray(input)) return c.fail(path, 'debe ser una lista de niveles');
  const ids: LevelId[] = [];
  let valid = true;
  input.forEach((item, index) => {
    const id = c.levelId(item, `${path}.${index}`);
    if (id === undefined) valid = false;
    else ids.push(id);
  });
  if (!valid) return undefined;
  if (new Set(ids).size !== ids.length) return c.fail(path, 'no admite niveles repetidos');
  if (!ids.includes('1-1')) return c.fail(path, 'debe incluir "1-1"');
  return ids;
}

function checkCompleted(c: Checker, input: unknown, path: string): SaveSlot['completedLevels'] | undefined {
  const o = c.object(input, path);
  if (!o) return undefined;
  const result: SaveSlot['completedLevels'] = {};
  let valid = true;
  for (const key of Object.keys(o)) {
    const record = isLevelId(key) ? checkRecord(c, o[key], `${path}.${key}`) : c.fail(`${path}.${key}`, 'nivel desconocido');
    if (record === undefined) valid = false;
    else result[key as LevelId] = record;
  }
  return valid ? result : undefined;
}

function checkCurrent(c: Checker, input: unknown, path: string): SaveSlot['current'] | undefined {
  const o = c.object(input, path);
  if (!o) return undefined;
  const levelId = c.levelId(o.levelId, `${path}.levelId`);
  let checkpointId: string | null | undefined;
  if (o.checkpointId === null || typeof o.checkpointId === 'string') checkpointId = o.checkpointId;
  else c.fail(`${path}.checkpointId`, 'debe ser una cadena o null');
  const lives = c.int(o.lives, `${path}.lives`, SAVE_LIMITS.livesMin, SAVE_LIMITS.livesMax);
  const dollars = c.int(o.dollars, `${path}.dollars`, 0, SAVE_LIMITS.dollarsMax);
  const score = c.int(o.score, `${path}.score`, 0);
  const scoreAtLevelStart = c.int(o.scoreAtLevelStart, `${path}.scoreAtLevelStart`, 0);
  const cleanRun = c.bool(o.cleanRun, `${path}.cleanRun`);
  if (
    levelId === undefined ||
    checkpointId === undefined ||
    lives === undefined ||
    dollars === undefined ||
    score === undefined ||
    scoreAtLevelStart === undefined ||
    cleanRun === undefined
  ) {
    return undefined;
  }
  return { levelId, checkpointId, lives, dollars, score, scoreAtLevelStart, cleanRun };
}

function checkStats(c: Checker, input: unknown, path: string): SaveSlot['stats'] | undefined {
  const o = c.object(input, path);
  if (!o) return undefined;
  const deaths = c.int(o.deaths, `${path}.deaths`, 0);
  const flaps = c.int(o.flaps, `${path}.flaps`, 0);
  const bossesDefeated = c.int(o.bossesDefeated, `${path}.bossesDefeated`, 0);
  const perfectJumps = c.int(o.perfectJumps, `${path}.perfectJumps`, 0);
  if (deaths === undefined || flaps === undefined || bossesDefeated === undefined || perfectJumps === undefined) {
    return undefined;
  }
  return { deaths, flaps, bossesDefeated, perfectJumps };
}

function checkSlot(c: Checker, input: unknown, path: string): SaveSlot | null | undefined {
  if (input === null) return null;
  const o = c.object(input, path);
  if (!o) return undefined;
  const createdAt = c.date(o.createdAt, `${path}.createdAt`);
  const updatedAt = c.date(o.updatedAt, `${path}.updatedAt`);
  const playTimeMs = c.int(o.playTimeMs, `${path}.playTimeMs`, 0);
  const unlockedLevels = checkUnlocked(c, o.unlockedLevels, `${path}.unlockedLevels`);
  const completedLevels = checkCompleted(c, o.completedLevels, `${path}.completedLevels`);
  const current = checkCurrent(c, o.current, `${path}.current`);
  const stats = checkStats(c, o.stats, `${path}.stats`);
  // Proposed integrity rule (@gdd-pending): the level being played must be unlocked.
  if (current && unlockedLevels && !unlockedLevels.includes(current.levelId)) {
    c.fail(`${path}.current.levelId`, 'debe estar en unlockedLevels');
    return undefined;
  }
  if (
    createdAt === undefined ||
    updatedAt === undefined ||
    playTimeMs === undefined ||
    unlockedLevels === undefined ||
    completedLevels === undefined ||
    current === undefined ||
    stats === undefined
  ) {
    return undefined;
  }
  return { createdAt, updatedAt, playTimeMs, unlockedLevels, completedLevels, current, stats };
}

/**
 * Runtime check of a parsed save document (GDD §6.4). Never throws: any value outside the schema comes back
 * as errors with their path. Unknown fields are dropped from the returned copy.
 */
export function validateSave(input: unknown): SaveValidation {
  const c = new Checker();
  const root = c.object(input, '');
  if (!root) return { ok: false, errors: c.errors };

  if (root.schemaVersion !== SCHEMA_VERSION) c.fail('schemaVersion', `debe ser ${SCHEMA_VERSION}`);

  let lastSlotId: SlotId | null | undefined;
  if (root.lastSlotId === null || (SLOT_IDS as readonly unknown[]).includes(root.lastSlotId)) {
    lastSlotId = root.lastSlotId as SlotId | null;
  } else {
    c.fail('lastSlotId', `debe ser null o uno de ${SLOT_IDS.join(', ')}`);
  }

  const settings = checkSettings(c, root.settings, 'settings');

  const slotsInput = c.object(root.slots, 'slots');
  const slots: Partial<Record<SlotId, SaveSlot | null>> = {};
  if (slotsInput) {
    for (const id of SLOT_IDS) {
      const slot = id in slotsInput ? checkSlot(c, slotsInput[id], `slots.${id}`) : c.fail(`slots.${id}`, 'falta el expediente');
      if (slot !== undefined) slots[id] = slot;
    }
  }

  if (c.errors.length > 0 || settings === undefined || lastSlotId === undefined) {
    return { ok: false, errors: c.errors };
  }
  return {
    ok: true,
    value: { schemaVersion: SCHEMA_VERSION, lastSlotId, settings, slots: slots as SaveFile['slots'] },
  };
}
